import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET, PARTICULAR_REQUEST_PARAMETER_LIST_GET } from '../../../../api/ApiConst';
import ApiCaller from '../../../../api/ApiCaller';
import ResponseParametersForm from '../components/ResponseParametersForm';
import UpdateConditionForm from '../components/UpdateConditionForm';

const DeleteAction = ({ 
    formData, 
    setFormData, 
    allTableList, 
    selectedRequest, 
    allPreviousSteps, 
    currentStepId 
}) => {
    const [tableFieldOptions, setTableFieldOptions] = useState([]);
    const [valueParameters, setValueParameters] = useState([]);
    const [parametersLoading, setParametersLoading] = useState(false);
    const [requestParamSource, setRequestParamSource] = useState(
        Array.isArray(formData?.requestParamSource) ? formData.requestParamSource : ['custom_request']
    );
    const [responseParameters, setResponseParameters] = useState([]);

    // Memoize tableId to prevent unnecessary effect re-runs
    const tableId = useMemo(() => formData?.tableId, [formData?.tableId]);

    // Handle formData updates (when editing existing steps)
    useEffect(() => {
        if (formData?.requestParamSource && Array.isArray(formData.requestParamSource)) {
            setRequestParamSource(formData.requestParamSource);
        }
        if (formData?.responseParameters && Array.isArray(formData.responseParameters)) {
            setResponseParameters(formData.responseParameters);
        }
    }, [formData]);

    // Load selected table's fields for the condition field dropdown
    useEffect(() => {
        if (!tableId) {
            setTableFieldOptions([]);
            return;
        }

        const requestBody = {
            page: 0,
            size: 1000,
            searchFilters: [],
            sortRules: [],
        };
        const apiUrl = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll('{dynamic-table-id}', tableId);

        ApiCaller.callPostApi(
            apiUrl,
            requestBody,
            {},
            (response) => {
                const attrs = Array.isArray(response?.data) ? response.data : [];
                // Map to Select options; use attribute name as value for delete conditions
                const options = attrs.map(a => ({ value: a.name, label: a.name }));
                setTableFieldOptions(options);
            },
            () => setTableFieldOptions([])
        );
    }, [tableId]);

    // Memoize sources to prevent unnecessary effect re-runs
    const requestParamSources = useMemo(() => 
        Array.isArray(formData?.requestParamSource) ? formData.requestParamSource : requestParamSource, 
        [formData?.requestParamSource, requestParamSource]
    );

    // Build value parameters list from selected requestParamSource
    useEffect(() => {
        if (requestParamSources.length === 0) {
            setValueParameters([]);
            return;
        }

        let combined = [];
        setParametersLoading(true);

        const addCustomRequestParams = () => new Promise((resolve) => {
            const requestId = selectedRequest;
            if (!requestId) return resolve();

            const apiUrl = PARTICULAR_REQUEST_PARAMETER_LIST_GET.replaceAll('{request-id}', requestId);
            ApiCaller.callPostApi(
                apiUrl,
                null,
                {},
                (response) => {
                    const data = Array.isArray(response?.data) ? response.data : [];
                    const opts = data.map(p => ({
                        value: `custom_request.${p.name}`,
                        label: `${p.name} (Request)`,
                        type: p.type,
                        attributes: Array.isArray(p?.config?.attributes) ? p.config.attributes.map(a => ({ name: a.name, type: a.type })) : []
                    }));
                    combined = combined.concat(opts);
                    resolve();
                },
                () => resolve()
            );
        });

        const addStepParams = () => {
            const stepOpts = [];
            requestParamSources.forEach(source => {
                if (source !== 'custom_request') {
                    const stepId = source.toString().startsWith('step_') ? source.replace('step_', '') : source;
                    const step = allPreviousSteps?.find(s => s.id === parseInt(stepId));
                    const params = step?.actions?.[0]?.config?.responseParameters || step?.action?.config?.responseParameters || [];
                    params.forEach(param => {
                        if (!param) return;
                        const name = param.name || param.id || '';
                        if (!name) return;
                        stepOpts.push({
                            value: `step_${stepId}.${name}`,
                            label: `${name} (Step ${stepId})`,
                            type: param.type,
                            attributes: Array.isArray(param?.config?.attributes) ? param.config.attributes.map(a => ({ name: a.name, type: a.type })) : []
                        });
                    });
                }
            });
            combined = combined.concat(stepOpts);
        };

        (async () => {
            if (requestParamSources.includes('custom_request')) {
                await addCustomRequestParams();
            }
            addStepParams();
            setValueParameters(combined);
            setParametersLoading(false);
        })();

    }, [requestParamSources, selectedRequest, allPreviousSteps]);

    // Event Handlers
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleRequestParamSourceChange = (selectedOptions) => {
        const values = selectedOptions ? selectedOptions?.map(option => option?.value) : ['custom_request'];
        setRequestParamSource(values);
        setFormData((prev) => ({
            ...prev,
            requestParamSource: values,
        }));
    };

    // Render Components
    const renderTableSelector = () => (
        <Form.Group>
            <Form.Label>Table</Form.Label>
            <Form.Control 
                as="select" 
                name="tableId" 
                value={formData?.tableId || ''} 
                onChange={handleChange}
            >
                <option value="">Select Table</option>
                {allTableList?.map((table) => (
                    <option key={table?.id} value={table?.id}>
                        {table?.name}
                    </option>
                ))}
            </Form.Control>
        </Form.Group>
    );

    const renderRequestParameterSource = () => (
        <Form.Group className="mt-3">
            <Form.Label>Request Parameter Source</Form.Label>
            <Select
                isMulti
                options={[
                    { value: 'custom_request', label: 'Custom Request' },
                    ...(allPreviousSteps || [])?.map((step) => ({ 
                        value: step.id, 
                        label: "Step " + step.id 
                    })),
                ]}
                value={
                    requestParamSources
                        ? requestParamSources?.map(source =>
                            source === 'custom_request'
                                ? { value: 'custom_request', label: 'Custom Request' }
                                : allPreviousSteps
                                    ?.map((step) => ({ value: step.id, label: "Step " + step.id }))
                                    ?.find((opt) => opt.value === source)
                        ).filter(Boolean)
                        : []
                }
                onChange={handleRequestParamSourceChange}
                isClearable={false}
                className="react-select-container"
            />
        </Form.Group>
    );

    // Memoize props to prevent unnecessary re-renders of UpdateConditionForm
    const conditionFormProps = useMemo(() => ({
        formData,
        setFormData,
        requestParameters: tableFieldOptions,
        valueParameters,
        parametersLoading
    }), [formData, setFormData, tableFieldOptions, valueParameters, parametersLoading]);

    return (
        <>
            {renderTableSelector()}
            {renderRequestParameterSource()}
            <hr style={{ margin: "20px 0 30px" }} />
            <UpdateConditionForm {...conditionFormProps} />
            <hr style={{ margin: "20px 0 30px" }} />
            <ResponseParametersForm
                responseParameters={responseParameters}
                setResponseParameters={setResponseParameters}
                formData={formData}
                setFormData={setFormData}
            />
        </>
    );
};

export default DeleteAction; 